// Plans & billing (issue #12). Stripe-hosted Checkout + Customer Portal —
// no card data ever touches our surface. Plan truth = users/{uid}/billing/state.
import { useEffect, useState } from "react";
import { createCheckout, deleteAccount, openPortal, watchBilling, type Billing } from "../api";
import { EVENTS, track } from "../analytics";
import { logout, useAuth } from "../auth";

const PLANS = [
  {
    id: "free" as const, name: "Free", price: "$0", blurb: "One full-quality kit a week — the magic trick, on us.",
    features: ["1 kit per week", "Print-ready PDFs", "Kid library (1 kid)"], cta: null,
  },
  {
    id: "family" as const, name: "Family", price: "$6.99/mo", yearly: "$49/yr",
    blurb: "Every weekly list, all year.", features: ["Unlimited kits", "1 kid", "Booklet every week"],
  },
  {
    id: "familyplus" as const, name: "Family+", price: "$9.99/mo",
    blurb: "The whole crew.", features: ["Unlimited kits", "Up to 4 kids", "Booklet every week"],
  },
];

export default function Pricing() {
  const { uid } = useAuth();
  const [billing, setBilling] = useState<Billing | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => watchBilling(setBilling), []);

  async function checkout(plan: "family" | "familyplus", interval: "monthly" | "yearly") {
    // #17: funnel — plan intent captured before the Stripe handoff.
    track(EVENTS.checkoutStart, { plan, interval });
    setBusy(`${plan}:${interval}`); setError("");
    try { window.location.assign(await createCheckout(plan, interval)); }
    catch (e) { setError(String((e as { message?: string }).message ?? e)); setBusy(""); }
  }

  async function portal() {
    setBusy("portal"); setError("");
    try { window.location.assign(await openPortal()); }
    catch (e) { setError(String((e as { message?: string }).message ?? e)); setBusy(""); }
  }

  const plan = billing?.plan ?? "free";

  return (
    <>
      <h1>Plans</h1>
      {uid && (
        <p className="muted">
          Current plan: <strong>{plan}</strong>
          {plan !== "free" && billing?.status ? ` (${billing.status})` : ""}
          {billing?.hasCustomer && (
            <> · <button className="linklike" disabled={busy === "portal"} onClick={portal}>Manage billing</button></>
          )}
        </p>
      )}
      {error && <p className="err">{error}</p>}
      <div className="plans">
        {PLANS.map((p) => (
          <div className="card plan" key={p.id}>
            <h2>{p.name}</h2>
            <p className="plan-price">{p.price}{p.yearly ? <> · <span className="muted">{p.yearly}</span></> : null}</p>
            <p>{p.blurb}</p>
            <ul>{p.features.map((f) => <li key={f}>{f}</li>)}</ul>
            {p.id === plan ? (
              <p className="muted">Your plan</p>
            ) : p.id !== "free" ? (
              <>
                <button disabled={busy !== ""} onClick={() => checkout(p.id as "family" | "familyplus", "monthly")}>
                  {busy === `${p.id}:monthly` ? "Redirecting…" : `Choose ${p.name}`}
                </button>
                {p.yearly && (
                  <button className="secondary" disabled={busy !== ""} onClick={() => checkout(p.id as "family", "yearly")}>
                    {busy === `${p.id}:yearly` ? "Redirecting…" : "Choose yearly (save ~41%)"}
                  </button>
                )}
              </>
            ) : null}
          </div>
        ))}
      </div>
      <p className="muted">Cancel anytime from the billing page. Kids cap per plan is enforced at kit creation;
        your free kit stays yours whatever plan you're on.</p>
      {uid && <DangerZone />}
    </>
  );
}

function DangerZone() {
  const [confirming, setConfirming] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function erase() {
    setBusy(true); setError("");
    try {
      await deleteAccount();
      await logout();
      window.location.assign("/");
    } catch (e) {
      setError(String((e as { message?: string }).message ?? e));
      setBusy(false);
    }
  }

  return (
    <section className="danger-zone">
      <h2>Account</h2>
      <p className="muted">Deleting your account removes everything: photos, kits, kid profiles, billing
        history — immediately and irreversibly. Any active subscription is canceled at once (no partial-period
        refund). Receipts stay in Stripe's records as financial law requires, but unlinked from you.</p>
      {!confirming ? (
        <button className="danger" onClick={() => setConfirming(true)}>Delete account</button>
      ) : (
        <div>
          <p>Type <strong>DELETE</strong> to confirm — this cannot be undone:</p>
          <input value={text} onChange={(e) => setText(e.target.value)} aria-label="Confirm deletion" />
          <button className="danger" disabled={text !== "DELETE" || busy} onClick={erase}>
            {busy ? "Deleting…" : "Delete everything"}
          </button>
          <button className="secondary" disabled={busy} onClick={() => setConfirming(false)}>Keep my account</button>
        </div>
      )}
      {error && <p className="err">Deletion failed: {error}</p>}
    </section>
  );
}
